import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { format, startOfDay, endOfDay } from "date-fns";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  name: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  isCheckedIn: PropTypes.bool,
  checkedInAt: PropTypes.string,
  method: PropTypes.string,
  originalData: PropTypes.object,
});

// Define individual Cell components with PropTypes
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const MemberCell = ({ value, row }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDAvatar name={value} size="sm" />
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {value}
      </MDTypography>
      <MDTypography variant="caption">{row.original.email || "member@example.com"}</MDTypography>
    </MDBox>
  </MDBox>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const AttendanceStatusCell = ({ value }) => (
  <MDBox display="flex" justifyContent="center">
    {value ? (
      <CheckIcon color="success" fontSize="medium" />
    ) : (
      <CloseIcon color="error" fontSize="medium" />
    )}
  </MDBox>
);
AttendanceStatusCell.propTypes = {
  value: PropTypes.bool.isRequired,
};

const DateTimeCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value === "Not checked in" || value === "Absent" ? value : new Date(value).toLocaleString()}
  </MDTypography>
);
DateTimeCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const MethodCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "biometric" ? "success" : value === "manual" ? "info" : "warning"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
MethodCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete, takeAttendance, takeManualAttendance }) => {
  const isCheckedIn = row.original.isCheckedIn;
  const isAbsent = row.original.checkedInAt === "Absent";

  return (
    <MDBox display="flex" justifyContent="center" gap={1}>
      {/* <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => handleEdit(row.original)}
      >
        <Icon>edit</Icon>&nbsp;Edit
      </MDButton>
      <MDButton
        variant="gradient"
        color="error"
        size="small"
        onClick={() => handleDelete(row.original)}
      >
        <Icon>delete</Icon>&nbsp;Delete
      </MDButton> */}
      {/* <MDButton
        variant="gradient"
        color={isCheckedIn ? "info" : isAbsent ? "warning" : "success"}
        size="small"
        onClick={() => !isAbsent && takeAttendance(row.original)}
        disabled={isCheckedIn || isAbsent}
      >
        <Icon>fingerprint</Icon>&nbsp;
        {isAbsent ? "Absent" : isCheckedIn ? "Checked In" : "Check In"}
      </MDButton> */}
      <MDButton
        variant="gradient"
        color={isCheckedIn ? "info" : isAbsent ? "warning" : "primary"}
        size="small"
        onClick={() => !isAbsent && takeManualAttendance(row.original)}
        disabled={isCheckedIn || isAbsent}
      >
        <Icon>fingerprint</Icon> <Icon>person</Icon>&nbsp;
        {isAbsent ? "Absent" : isCheckedIn ? "Checked In" : "Manual"}
      </MDButton>
    </MDBox>
  );
};
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  takeAttendance: PropTypes.func.isRequired,
  takeManualAttendance: PropTypes.func.isRequired,
};

export default function useMinistryMembersData(ministryId) {
  const { user, church } = useAuth();
  const [showAttendanceView, setShowAttendanceView] = useState(false);
  const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [memberData, setMemberData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "MemberID",
        accessor: "memberId",
        align: "left",
        width: "30%",
        Cell: MemberCell,
      },
      {
        Header: "Member",
        accessor: "name",
        align: "left",
        width: "30%",
        Cell: MemberCell,
      },
      {
        Header: "Phone",
        accessor: "phone",
        align: "left",
        width: "20%",
      },
      {
        Header: "Residential Address",
        accessor: "residential_address",
        align: "left",
        width: "20%",
      },
      {
        Header: "Occupation",
        accessor: "occupation",
        align: "left",
        width: "20%",
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            takeAttendance={takeAttendance}
            takeManualAttendance={takeManualAttendance}
          />
        ),
      },
    ],
    attendanceColumns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "MemberID", accessor: "memberId", align: "left", width: "25%" },
      { Header: "Member", accessor: "name", align: "left", width: "25%", Cell: MemberCell },
      { Header: "Phone", accessor: "phone", align: "left", width: "15%" },
      { Header: "Email", accessor: "email", align: "left", width: "15%" },
      {
        Header: "Checked In",
        accessor: "isCheckedIn",
        align: "center",
        width: "10%",
        Cell: AttendanceStatusCell,
      },
      {
        Header: "Checked In At",
        accessor: "checkedInAt",
        align: "left",
        width: "15%",
        Cell: DateTimeCell,
      },
      {
        Header: "Method",
        accessor: "method",
        align: "center",
        width: "10%",
        Cell: MethodCell,
      },
    ],
    rows: [],
    attendanceRows: [],
    isLoading: true,
    error: null,
  });

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const handleEdit = (member) => {
    setSelectedMember(member);
    setEditModalOpen(true);
  };

  const handleDelete = (member) => {
    setSelectedMember(member);
    setDeleteModalOpen(true);
  };

  const takeAttendance = async (memberData) => {
    try {
      if (!memberData?.id) {
        throw new Error("Invalid member data");
      }

      if (!church?.id) {
        throw new Error("Church context is not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(`https://api.ofi-cia.com/api/attendance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberData.id,
          method: "biometric",
          church_id: church.id,
          ministry_id: ministryId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Attendance operation failed");
      }

      await Swal.fire({
        title: "Success!",
        text: "Attendance recorded successfully",
        icon: "success",
      });

      fetchMinistryMembers();
      fetchMinistryAttendance();
    } catch (error) {
      console.error("Attendance error:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to record attendance",
        icon: "error",
      });
    }
  };

  const takeManualAttendance = async (memberData) => {
    try {
      if (!memberData?.id) {
        throw new Error("Invalid member data");
      }

      if (!church?.id) {
        throw new Error("Church context is not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(`https://api.ofi-cia.com/api/attendance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberData.id,
          method: "manual",
          church_id: church.id,
          ministry_id: ministryId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Manual attendance operation failed");
      }

      await Swal.fire({
        title: "Success!",
        text: "Manual attendance recorded successfully",
        icon: "success",
      });

      fetchMinistryMembers();
      fetchMinistryAttendance();
    } catch (error) {
      console.error("Manual attendance error:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to record manual attendance",
        icon: "error",
      });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedMember.id
        ? `https://api.ofi-cia.com/api/members/${selectedMember.id}`
        : "https://api.ofi-cia.com/api/members";

      const method = selectedMember.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedMember.name,
          ministry_id: ministryId,
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Member successfully ${selectedMember.id ? "updated" : "added"}`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchMinistryMembers();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Member operation failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/members/${selectedMember.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Member deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchMinistryMembers();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleAddMember = () => {
    setSelectedMember({
      id: null,
      name: "",
      ministry_id: ministryId,
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleAttendanceView = () => {
    setShowAttendanceView(!showAttendanceView);
    if (!showAttendanceView) {
      fetchMinistryAttendance();
    }
  };

  const fetchMinistryMembers = async () => {
    try {
      setMemberData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        `https://api.ofi-cia.com/api/ministries/members/${church.id}/${ministryId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const members = result.data || result.members || result;

      if (!Array.isArray(members)) {
        throw new Error("API response is not an array");
      }

      setMemberData((prev) => ({
        ...prev,
        rows: members.map((member, index) => ({
          id: member.id,
          serial: index + 1,
          memberId: member.memberID || "N/A",
          name: `${member.first_name} ${member.surname}`,
          phone: member.phone,
          email: member.email,
          residential_address: member.residential_address || "N/A",
          occupation: member.occupation || "N/A",
          status: member.status ? "active" : "inactive",
          createdAt: member.created_at,
          isCheckedIn: member.is_checked_in || false,
          checkedInAt: member.checked_in_at || "Not checked in",
          method: member.method || "N/A",
          originalData: member,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setMemberData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const fetchMinistryAttendance = async () => {
    try {
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const formatDate = (date) => format(date, "yyyy-MM-dd");
      const start = formatDate(dateRange.startDate);
      const end = formatDate(dateRange.endDate);

      const response = await fetch(
        `https://api.ofi-cia.com/api/ministries/attendance/${church.id}/${ministryId}?start_date=${start}&end_date=${end}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const attendanceRecords = result.attendance || result.data || [];

      if (!Array.isArray(attendanceRecords)) {
        throw new Error("API response is not an array");
      }

      setMemberData((prev) => ({
        ...prev,
        attendanceRows: attendanceRecords.map((record, index) => ({
          id: record.member_id || record.id,
          serial: index + 1,
          name:
            record.member_name ||
            `${record.first_name || ""} ${record.surname || ""}`.trim() ||
            "Unknown Member",
          phone: record.phone || null,
          email: record.email || null,
          isCheckedIn: record.status === "Check-out" || record.status === 0,
          checkedInAt:
            record.checked_in_at || record.check_time || record.timestamp || "Not checked in",
          method: record.method || "biometric",
          originalData: record,
        })),
      }));
    } catch (error) {
      console.error("Fetch attendance failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load attendance data",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    fetchMinistryMembers();
  }, [user, church, ministryId]);

  const filteredAttendanceRows = showOnlyUnchecked
    ? memberData.attendanceRows.filter((row) => !row.isCheckedIn)
    : memberData.attendanceRows;

  return {
    ...memberData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    takeAttendance,
    takeManualAttendance,
    showAttendanceView,
    setShowAttendanceView,
    toggleAttendanceView,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    dateRange,
    setDateRange,
    fetchMinistryAttendance,
    filteredAttendanceRows,
  };
}
